import React, { useCallback, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Toolbar } from '@mui/material';
import Header from './Header';
import Sidebar from './Sidebar';
import NotionStyleChatPanel from '../chat/NotionStyleChatPanel';
import { useAppDispatch } from '@store/index';
import { setChatOpen, setChatWidth } from '@features/ui/uiSlice';
import { useResponsiveLayout } from '@hooks/useResponsiveLayout';
import { LAYOUT } from '../../constants/layout';

const MainLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const layout = useResponsiveLayout();

  const handleChatClose = () => {
    dispatch(setChatOpen(false));
  };

  // Drag resize for chat panel
  const isDragging = useRef(false);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    const startX = e.clientX;
    const startWidth = layout.chatWidth || LAYOUT.CHAT_PANEL_WIDTH.md;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = startX - ev.clientX;
      const newWidth = Math.max(280, Math.min(800, startWidth + delta));
      dispatch(setChatWidth(newWidth));
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [dispatch, layout.chatWidth]);

  const effectiveChatWidth = layout.chatIsOverlay ? 0 : layout.chatWidth;

  return (
    <Box sx={{ display: 'flex', position: 'relative', minHeight: '100vh' }}>
      <Header sidebarWidth={layout.sidebarWidth} />
      <Sidebar
        sidebarMode={layout.sidebarMode}
        isMobile={layout.isMobile}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: LAYOUT.MAIN_CONTENT_PADDING,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          marginRight: `${effectiveChatWidth}px`,
          width: `calc(100% - ${layout.sidebarWidth}px - ${effectiveChatWidth}px)`,
        }}
      >
        <Toolbar />
        <Box
          sx={{
            maxWidth: layout.mainContentMaxWidth,
            mx: layout.mainContentMaxWidth ? 'auto' : undefined,
          }}
        >
          <Outlet />
        </Box>
      </Box>
      {/* Drag handle for resizing chat panel */}
      {layout.chatOpen && !layout.chatIsOverlay && (
        <Box
          onMouseDown={handleDragStart}
          sx={{
            position: 'fixed',
            right: (layout.chatWidth || LAYOUT.CHAT_PANEL_WIDTH.md) - 3,
            top: LAYOUT.HEADER_HEIGHT,
            bottom: 0,
            width: 6,
            cursor: 'col-resize',
            zIndex: 1201,
            '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
            transition: 'background-color 0.15s',
          }}
        />
      )}
      <NotionStyleChatPanel
        open={layout.chatOpen}
        onClose={handleChatClose}
        width={layout.chatWidth || LAYOUT.CHAT_PANEL_WIDTH.md}
        isOverlay={layout.chatIsOverlay}
      />
    </Box>
  );
};

export default MainLayout;
